/* eslint-disable no-unsafe-optional-chaining */
import React, { useState, useEffect, Fragment } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import axios from 'axios'
import Cookies from 'js-cookie'
import { bool, string, object, array, shape, arrayOf, number } from 'prop-types'

import useGenerationStatusWatcher from 'Pods/Shared/GenerationStatus/useGenerationStatusWatcher'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import useViewerId from 'Pods/Shared/Hooks/useViewerId'
import UserSegmentationModal from 'Pods/Shared/UserSegmentationModal'
import UserSubscriptionsModal from 'Pods/Shared/UserSubscriptionsModal'
import SaleOffBar from 'Pods/TopNotificationBars/Components/SaleOffBar'
import SubscribeBar, { DEFAULT_SUBSCRIPTION_PRICE } from 'Pods/TopNotificationBars/Components/SubscribeBar'

import { sendExperimentEvent } from 'Utils/Experiments'

import LegacyNavigation from './Components/LegacyNavigation/LegacyNavigation'
import Navigation from './Components/Navigation/Navigation'

const navId = 'navigation-header'

// Studio tiers where the Create with AI "Free trial" tag shows: the viewer is in
// a trial, can start one, or can still reach one via the waitlist. Mirrors
// Generator::SubscriptionState::FREE_TRIAL_TAG_TIERS (slow-page path).
const FREE_TRIAL_TAG_TIERS = ['in_free_beta', 'eligible_for_trial', 'must_join_waitlist', 'waitlisted']

function UnifiedNavigation({
  extractingCoreStyles = false,
  unifiedNavData = {
    barForbidden: false,
    commonRoutes: {},
    mainMenuItems: {},
    locale: 'en',
    byType: [],
  },
  isInFastPage = false,
  isInListingPage = false,
  searchInputValue = '',
  // Actorless SSR baseline (boolean gate) on fast pages; refined per-user from pageConfig below.
  newTopNavigation: slowNewTopNavigation = false,
  // Raw six_digit_auth_flow assignment on slow pages (null when unbucketed); fast pages resolve it from pageConfig.
  newTopNavigationExperimentValue: slowNewTopNavigationExperimentValue = null,
  // props from slow pages
  isSignedIn = false,
  currentUserId: slowCurrentUserId = null,
  lastTimeModalSeenCookieKey,
  displaySegmentationModal,
  subscriptionsModalClosedCookieKey,
  displaySubscriptionsModal,
  barForbidden,
  newRoyaltyUi: slowRoyaltyUi,
  showCartAds: slowShowCartAds = false,
  useAdvancedKeywordsSearch: slowUseAdvancedKeywordsSearch = false,
  // sale off bar props
  discountConfig = null,
  subscriptionTopbar: slowSubscriptionTopbar = false,
  subscriptionPrice = DEFAULT_SUBSCRIPTION_PRICE,
  isActiveSubscriber: slowIsActiveSubscriber = false,
  subscriptionMenuEnabled: slowSubscriptionMenuEnabled = false,
  subscriptionFlowEnabled: slowSubscriptionFlowEnabled = false,
  showFreeTrialTag: slowShowFreeTrialTag = false,
}) {
  const {
    commonRoutes,
    mainMenuItems,
    legacyMainMenuItems,
    communityLinks,
    createAiLinks,
    createAiGeneration,
    actionsLinks,
    locale,
    cldGiftSize,
    assetCategories,
    printableCategories,
    searchPlaceholder,
    byType,
    budgetsData,
  } = unifiedNavData

  const pageConfig = usePageConfig({ listener: 'full-page-config-loaded', checkIfAvailable: false })
  const [subscriptionTopbar, setSubscriptionTopbar] = useState(slowSubscriptionTopbar)
  const [newTopNavigation, setNewTopNavigation] = useState(slowNewTopNavigation)
  // Logged-out preview opt-in (armed by the `new_login_preview` cookie). False at SSR to
  // match the actorless fast-page baseline; flipped on post-hydration in the effect below.
  const [newLoginPreview, setNewLoginPreview] = useState(false)
  const showSubscriptionBar = !!subscriptionTopbar && !barForbidden
  const showSaleOff = !showSubscriptionBar && !!discountConfig && !barForbidden
  const [actionsConfig, setActionsConfig] = useState({
    isCurrentUser: isSignedIn,
    cart: {},
    inboxSize: 0,
    notificationsSize: 0,
    newRoyaltyUi: slowRoyaltyUi,
    showCartAds: slowShowCartAds,
    subscriptionTopbar: slowSubscriptionTopbar,
    isActiveSubscriber: slowIsActiveSubscriber,
    subscriptionMenuEnabled: slowSubscriptionMenuEnabled,
    subscriptionFlowEnabled: slowSubscriptionFlowEnabled,
  })
  const [enableAdvancedKeywordsSearch, setEnableAdvancedKeywordsSearch] = useState(slowUseAdvancedKeywordsSearch)
  // Free-trial tag in the Create with AI dropdown. On slow pages `current_user` is
  // available at SSR, so it arrives as a slow prop; on fast pages (no actor at SSR)
  // it is resolved from the per-user studio tier in pageConfig via readPageConfig.
  const [showFreeTrialTag, setShowFreeTrialTag] = useState(slowShowFreeTrialTag)

  const isInSlowPage = !isInFastPage

  // Viewer identity for the generation-status nav, normalized to a string. Slow pages
  // deliver the signed-in id deterministically as an SSR prop (current_user is available
  // there); fast pages have no actor at SSR, so it resolves client-side via useViewerId
  // once /load-services.js lands. Until it resolves the nav withholds any persisted run,
  // so a shared browser never leaks a generation across accounts.
  const clientViewerId = useViewerId()
  const currentUserId = slowCurrentUserId != null ? String(slowCurrentUserId) : clientViewerId

  // Site-wide producer that keeps the global generation-status store fed off /ai too.
  useGenerationStatusWatcher({ isCurrentUser: actionsConfig.isCurrentUser })

  function setToken() {
    const metaToken = document.getElementsByName('csrf-token')[0]
    let token = null

    if (metaToken) {
      token = metaToken.getAttribute('content')
      axios.defaults.headers.common['X-CSRF-Token'] = token
    }

    axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'
  }

  useEffect(() => {
    if (isInSlowPage) {
      setActionsConfig({
        isCurrentUser: isSignedIn,
        cart: {},
        inboxSize: 0,
        notificationsSize: 0,
        newRoyaltyUi: slowRoyaltyUi,
        showCartAds: slowShowCartAds,
        subscriptionTopbar: slowSubscriptionTopbar,
        isActiveSubscriber: slowIsActiveSubscriber,
        subscriptionMenuEnabled: slowSubscriptionMenuEnabled,
        subscriptionFlowEnabled: slowSubscriptionFlowEnabled,
      })
    }
  }, [])

  useEffect(() => {
    if (!isInSlowPage && pageConfig) {
      readPageConfig()
    }
  }, [isInSlowPage, pageConfig])

  useEffect(() => {
    if (Cookies.get('new_login_preview')) {
      setNewLoginPreview(true)
    }
  }, [])

  useEffect(() => {
    if (pageConfig) {
      sendExperimentEvent('disable_sitewide_discounts', pageConfig.disableSitewideDiscounts)
    }
  }, [pageConfig])

  // six_digit_auth_flow exposure (new top navigation A/B test). Slow pages deliver the raw
  // assignment as an SSR prop; fast pages resolve it per-user once /load-services.js lands.
  // sendExperimentEvent no-ops on null/undefined, so unbucketed and flag-forced users never report.
  useEffect(() => {
    if (isInSlowPage) {
      sendExperimentEvent('six_digit_auth_flow', slowNewTopNavigationExperimentValue)
    } else if (pageConfig) {
      sendExperimentEvent('six_digit_auth_flow', pageConfig.newTopNavigationExperimentValue)
    }
  }, [pageConfig])

  useEffect(() => {
    const appHeader = document.getElementById('app-header')
    const notificationsContainer = document.getElementById('notifications-container')
    const hasTopBar = showSubscriptionBar || showSaleOff

    if (appHeader) {
      appHeader.classList.toggle('with-bar', extractingCoreStyles || hasTopBar)
    }

    if (notificationsContainer) {
      notificationsContainer.classList.toggle('with-bar', hasTopBar)
    }
  }, [extractingCoreStyles, showSubscriptionBar, showSaleOff])

  function readPageConfig() {
    setToken()

    const {
      cart,
      inboxSize,
      notificationsSize,
      isCurrentUser,
      newRoyaltyUi,
      showCartAds,
      useAdvancedKeywordsSearch,
      subscriptionTopbar: configSubscriptionTopbar,
      newTopNavigation: configNewTopNavigation,
      isActiveSubscriber: configIsActiveSubscriber,
      subscriptionMenuEnabled: configSubscriptionMenuEnabled,
      subscriptionFlowEnabled: configSubscriptionFlowEnabled,
      threeDStudioAccessTier,
    } = pageConfig

    setShowFreeTrialTag(FREE_TRIAL_TAG_TIERS.includes(threeDStudioAccessTier))

    window.dispatchEvent(
      new CustomEvent('navigation-cart-loaded', {
        detail: cart,
      })
    )

    setEnableAdvancedKeywordsSearch(useAdvancedKeywordsSearch)
    setSubscriptionTopbar(configSubscriptionTopbar)
    setNewTopNavigation(typeof configNewTopNavigation === 'boolean' ? configNewTopNavigation : slowNewTopNavigation)

    setActionsConfig({
      cart,
      inboxSize,
      notificationsSize,
      isCurrentUser,
      newRoyaltyUi,
      showCartAds,
      subscriptionTopbar: configSubscriptionTopbar,
      subscriptionMenuEnabled: configSubscriptionMenuEnabled,
      subscriptionFlowEnabled: configSubscriptionFlowEnabled,
      isActiveSubscriber: configIsActiveSubscriber,
    })
  }

  return (
    <nav
      id={navId}
      className={classnames('wayfinder', (extractingCoreStyles || showSubscriptionBar || showSaleOff) && 'with-bar')}
    >
      {showSubscriptionBar && (
        <SubscribeBar price={subscriptionPrice} locale={locale} />
      )}

      {showSaleOff && (
        <SaleOffBar
          barColor={discountConfig.barColor}
          content={discountConfig.body}
          discountPhrase={discountConfig.discountPhrase}
          discount={discountConfig.noticeBarDiscount}
          endingDate={discountConfig.endingDate}
          useTimer={discountConfig.useTimer}
          locale={locale}
        />
      )}

      {newTopNavigation || newLoginPreview ? (
        <Navigation
          commonRoutes={commonRoutes}
          mainMenuItems={mainMenuItems}
          communityLinks={communityLinks}
          createAiLinks={createAiLinks}
          createAiGeneration={createAiGeneration}
          showFreeTrialTag={showFreeTrialTag}
          actionsLinks={actionsLinks}
          actionsConfig={actionsConfig}
          locale={locale}
          isInListingPage={isInListingPage}
          searchInputValue={searchInputValue}
          searchPlaceholder={searchPlaceholder}
          currentUserId={currentUserId}
          assetCategories={assetCategories}
          printableCategories={printableCategories}
          byType={byType}
          budgetsData={budgetsData}
        />
      ) : (
        <LegacyNavigation
          commonRoutes={commonRoutes}
          mainMenuItems={legacyMainMenuItems}
          communityLinks={communityLinks}
          actionsLinks={actionsLinks}
          actionsConfig={actionsConfig}
          locale={locale}
          cldGiftSize={cldGiftSize}
          isInListingPage={isInListingPage}
          searchInputValue={searchInputValue}
          printableCategories={printableCategories}
          assetCategories={assetCategories}
          enableAdvancedKeywordsSearch={enableAdvancedKeywordsSearch}
          extractingCoreStyles={extractingCoreStyles}
          searchPlaceholder={searchPlaceholder}
          byType={byType}
          budgetsData={budgetsData}
        />
      )}

      {isInSlowPage && (
        <Fragment>
          <UserSegmentationModal cookieKey={lastTimeModalSeenCookieKey} isModalOpen={displaySegmentationModal} />
          <UserSubscriptionsModal
            lastSeenCookieKey={lastTimeModalSeenCookieKey}
            modalClosedCookieKey={subscriptionsModalClosedCookieKey}
            isModalOpen={displaySubscriptionsModal}
          />
        </Fragment>
      )}
    </nav>
  )
}

UnifiedNavigation.propTypes = {
  extractingCoreStyles: bool,
  isInFastPage: bool,
  isInListingPage: bool,
  searchInputValue: string,
  newTopNavigation: bool,
  newTopNavigationExperimentValue: bool,
  unifiedNavData: shape({
    barForbidden: bool,
    commonRoutes: object,
    mainMenuItems: object,
    legacyMainMenuItems: object,
    communityLinks: array,
    createAiLinks: array,
    createAiGeneration: object,
    actionsLinks: arrayOf(shape({
      id: string,
      href: string,
      icon: string,
      data: shape({
        trackText: string,
        trackLabel: string,
        avoidNavigation: bool,
      }),
    })).isRequired,
    locale: string,
    assetCategories: object,
    printableCategories: object,
    budgetsData: shape({
      assetsBaseUrl: string.isRequired,
      printableBaseUrl: string.isRequired,
      assetsBudgets: arrayOf(shape({
        title: string.isRequired,
        path: string.isRequired,
        icon: string.isRequired,
        withSub: bool,
      })).isRequired,
      printableAssetsBudgets: arrayOf(shape({
        title: string.isRequired,
        path: string.isRequired,
        icon: string.isRequired,
        withSub: bool,
      })).isRequired,
    }).isRequired,
  }),
  isSignedIn: bool,
  currentUserId: number,
  showCartAds: bool,
  useAdvancedKeywordsSearch: bool,
  subscriptionTopbar: bool,
  subscriptionPrice: string,
  isActiveSubscriber: bool,
  subscriptionMenuEnabled: bool,
  subscriptionFlowEnabled: bool,
  showFreeTrialTag: bool,
  discountConfig: shape({
    barColor: string,
    body: string,
    discountPhrase: string,
    noticeBarDiscount: number,
    endingDate: number,
    useTimer: bool,
  }),
}

export default UnifiedNavigation
